/**
 * nss-buybox-discount.js
 *
 * Shared, framework-free resolver for the buybox discount-preview feature.
 * Reads Shopify's native `discount_code` cookie and resolves the applicable
 * discount chain from the server-rendered #nss-discount-data JSON (see
 * snippets/nss-buybox-discount-data.liquid): the first eligible automatic
 * discount stacks with the cookie-matched coded discount (code applied last),
 * then computes the discounted price + total savings %.
 *
 * This is DISPLAY ONLY — the real discount still applies at checkout via the
 * cookie. Exposes a guarded global so each buybox block can share one instance.
 */
(function () {
  if (window.NSS_DISCOUNT) return;

  function norm(v) {
    return String(v == null ? "" : v).trim();
  }

  /** Read a cookie by name; returns the decoded value or null. */
  function readCookie(name) {
    if (!document.cookie) return null;
    const target = name + "=";
    const parts = document.cookie.split("; ");
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(target) === 0) {
        const raw = parts[i].substring(target.length);
        try {
          return decodeURIComponent(raw);
        } catch (e) {
          return raw;
        }
      }
    }
    return null;
  }

  let _cache;
  /** Parse + memoize the server-rendered discount catalog. */
  function getActiveDiscounts() {
    if (_cache !== undefined) return _cache;
    const el = document.getElementById("nss-discount-data");
    if (!el) {
      _cache = [];
      return _cache;
    }
    try {
      _cache = JSON.parse((el.textContent || "").trim()) || [];
    } catch (e) {
      console.warn("[NSS][Discount] Could not parse #nss-discount-data", e);
      _cache = [];
    }
    return _cache;
  }

  function purchaseTypeMatches(discountType, currentMode) {
    // currentMode: "subscription" | "onetime"
    if (discountType === "Both") return true;
    if (discountType === "Subscriptions") return currentMode === "subscription";
    if (discountType === "One-time purchase") return currentMode === "onetime";
    return true; // unknown value → don't block
  }

  function appliesToMatches(d, ctx) {
    if (d.applies_to === "Specific products") {
      // Match the referenced variants ONLY. Falling back to the parent product
      // id would bleed a one-variant discount onto its sibling variants
      // (Shopify applies it to the selected variants alone).
      const vids = (d.product_variant_ids || []).map(norm);
      return vids.indexOf(norm(ctx.variantId)) !== -1;
    }
    if (d.applies_to === "Specific collections") {
      const cids = (d.collection_ids || []).map(norm);
      return (ctx.collectionIds || []).some(function (c) {
        return cids.indexOf(norm(c)) !== -1;
      });
    }
    // "All products" or unknown → applies everywhere
    return true;
  }

  function minimumMatches(d, ctx) {
    const min = parseInt(d.min, 10) || 0;
    if (d.min_type === "Quantity") return (ctx.quantity || 0) >= min;
    // "Amount ($)" minimum is stored in cents. PDP can't see the cart-wide
    // total, so we approximate with the current line subtotal.
    if (d.min_type === "Amount ($)") return (ctx.lineSubtotalCents || 0) >= min;
    return true; // "None"
  }

  /**
   * Find the applicable discount chain for the current buybox selection.
   * The first eligible automatic (no-cookie) discount and the cookie-matched
   * coded discount are ADDITIVE: both can apply, automatic first, cookie code
   * last. Returns null when nothing matches, else:
   *   { chain: [automatic?, coded?],   // application order
   *     display: coded || automatic }  // drives the code chip
   */
  function findMatch(ctx) {
    const discounts = getActiveDiscounts();
    if (!discounts.length) return null;
    const cookie = norm(ctx.cookieCode).toLowerCase();

    let automatic = null;
    let coded = null;
    for (let i = 0; i < discounts.length; i++) {
      const d = discounts[i];
      if (!purchaseTypeMatches(d.purchase_type, ctx.purchaseType)) continue;
      if (!appliesToMatches(d, ctx)) continue;
      if (!minimumMatches(d, ctx)) continue;

      if (norm(d.function) === "Automatic discount") {
        // Shopify auto-applies these — no cookie required.
        if (!automatic) automatic = d;
      } else {
        // "Discount cookie": requires the discount_code cookie to match.
        const code = norm(d.code);
        if (!coded && code && cookie && code.toLowerCase() === cookie) coded = d;
      }
    }
    if (!automatic && !coded) return null;

    const chain = [];
    if (automatic) chain.push(automatic);
    if (coded) chain.push(coded); // cookie code applies last
    return { chain: chain, display: coded || automatic };
  }

  /**
   * Stack a discount chain on top of the already sub+bundle-discounted price,
   * sequentially in the order given (automatic first, cookie code last).
   * Prices are in cents. Returns { newPriceCents, totalPct } or null.
   */
  function apply(opts) {
    const compare = parseInt(opts.compareCents, 10);
    let price = parseInt(opts.priceCents, 10);
    if (!compare || isNaN(price)) return null;

    const discounts = opts.discounts || [];
    for (let i = 0; i < discounts.length; i++) {
      const d = discounts[i];
      // parseFloat: percentage values may carry decimals (e.g. 7.39).
      // Sub-cent precision is kept between steps and rounded once at the end,
      // mirroring how Shopify stacks the real allocations.
      const value = parseFloat(d.value) || 0;
      if (d.value_type === "Fixed Amount") {
        price = price - value; // `value` is already in cents
      } else {
        // "Percentage" (default)
        price = (price * (100 - value)) / 100;
      }
      if (price < 0) price = 0;
    }
    price = Math.round(price);

    const totalPct = Math.round(((compare - price) / compare) * 100);
    return { newPriceCents: price, totalPct: totalPct };
  }

  window.NSS_DISCOUNT = {
    readCookie: readCookie,
    getActiveDiscounts: getActiveDiscounts,
    findMatch: findMatch,
    apply: apply,
  };
})();
